import { Box, Link, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/common/Button';
import DatePickerField from '../../../components/common/DatePickerField';
import Loading from '../../../components/common/Loading';
import SelectField from '../../../components/common/SelectField';
import StatusChip, { StatusConfig } from '../../../components/common/StatusChip';
import CustomTable from '../../../components/common/table/CustomTable';
import { Column } from '../../../components/common/table/types';
import TextField from '../../../components/common/TextField';
import api from '../../../utils/api';
import { formatDate } from '../../../utils/date';

interface VacancyApiResponse {
  count: number;
  data: VacancyData[];
}

interface VacancyData {
  id: number;
  vacancyType: string | null;
  vacancy_Code: string;
  vacancy_Title: string;
  countries: string | null;
  vacancy_StartDate: string;
  vacancy_EndDate: string;
  status: number;
  approved_by: string | null;
}

type VacancyColumn = Column<VacancyData>;

const vacancyStatusConfig: StatusConfig = {
  'Active': {
    label: 'Active',
    color: '#027A48',
    backgroundColor: '#ECFDF3'
  },
  'Inactive': {
    label: 'Inactive',
    color: '#00000066',
    backgroundColor: '#00000014'
  }
};

interface FetchVacanciesParams {
  vacancy_Code?: string;
  vacancy_Title?: string;
  vacancyType?: string;
  vacancy_StartDate?: string;
  vacancy_EndDate?: string;
  status?: string;
  countries?: string;
  page: number;
  limit: number;
  sort?: string;
}

const VacancySearch = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState<keyof VacancyData | undefined>('vacancy_Code');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchParams, setSearchParams] = useState({
    vacancyType: '',
    code: '',
    vacancyTitle: '',
    country: '',
    startDate: '',
    endDate: '',
    status: '',
  });
  const [vacancies, setVacancies] = useState<VacancyData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchVacancies = async () => {
      try {
        setIsLoading(true);

        const params: FetchVacanciesParams = {
          vacancy_Code: searchParams.code ? searchParams.code : undefined,
          vacancy_Title: searchParams.vacancyTitle ? searchParams.vacancyTitle : undefined,
          vacancyType: searchParams.vacancyType ? searchParams.vacancyType : undefined,
          vacancy_StartDate: searchParams.startDate ? searchParams.startDate : undefined,
          vacancy_EndDate: searchParams.endDate ? searchParams.endDate : undefined,
          status: searchParams.status === 'Active' ? '0' : searchParams.status === 'Inactive' ? '1' : '',
          countries: searchParams.country ? searchParams.country : undefined,
          page: page + 1, // API uses 1-based pagination
          limit: rowsPerPage,
          sort: sortField ? `${sortField}:${sortDirection}` : undefined
        };

        // Remove empty params and convert to Record<string, string>
        const queryParams: Record<string, string> = {};
        Object.entries(params).forEach(([key, value]) => {
          if (value !== '' && value !== undefined) {
            queryParams[key] = String(value);
          }
        });

        const response = await api.get<VacancyApiResponse>(
          `/rec/vacancy/search?${new URLSearchParams(queryParams)}`,
          {
            headers: {
              'x-api-id': 4,
              'x-page-id': 5,
              'x-token': 'PSBrwdyDEe4tP8E1X89lEjeD3e7IpebrFtypPjwFD9S7baxiRhIxP5dyLJuqrxV0'
            }
          }
        );

        const data = response.data;
        setTotalCount(data.count);
        setVacancies(data.data);
      } catch (error) {
        console.error('Error fetching vacancies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVacancies();
  }, [
    page,
    rowsPerPage,
    sortField,
    sortDirection,
    searchParams.code,
    searchParams.vacancyTitle,
    searchParams.vacancyType,
    searchParams.startDate,
    searchParams.endDate,
    searchParams.status,
    searchParams.country
  ]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
  };

  const handleReset = () => {
    setSearchParams({
      vacancyType: '',
      code: '',
      vacancyTitle: '',
      country: '',
      startDate: '',
      endDate: '',
      status: '',
    });
    setPage(0);
  };

  const handleSort = (field: keyof VacancyData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const columns: VacancyColumn[] = [
    {
      field: 'vacancy_Title',
      headerName: 'Vacancy Title',
      sortable: true,
      width: '200px',
      type: 'text',
      renderCell: (row: VacancyData) => (
        <div onClick={() => navigate(`/rec/vacancy/search/${row.id}/view`)} style={{ cursor: 'pointer' }}>{row.vacancy_Title}</div>
      )
    },
    {
      field: 'vacancy_Code',
      headerName: 'Code',
      sortable: true,
      width: '150px',
      type: 'text'
    },
    {
      field: 'vacancyType',
      headerName: 'Vacancy Type',
      sortable: true,
      width: '100px',
      type: 'text'
    },
    {
      field: 'countries',
      headerName: 'Country',
      sortable: true,
      width: '200px',
      type: 'text'
    },
    {
      field: 'vacancy_StartDate',
      headerName: 'Start Date',
      sortable: true,
      width: '75px',
      type: 'date',
      renderCell: (row: VacancyData) => formatDate(row.vacancy_StartDate),
    },
    {
      field: 'vacancy_EndDate',
      headerName: 'End Date',
      sortable: true,
      width: '40px',
      type: 'date',
      renderCell: (row: VacancyData) => formatDate(row.vacancy_EndDate),
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      width: '80px',
      type: 'status',
      renderCell: (row: VacancyData) => (
        <StatusChip
          status={row.status === 1 ? 'Active' : 'Inactive'}
          statusConfig={vacancyStatusConfig}
        />
      ),
    },
    {
      field: 'approved_by',
      headerName: 'Approved By',
      sortable: true,
      width: '150px',
      type: 'text'
    },
    {
      field: 'actions',
      headerName: '',
      width: '300px',
      sortable: false,
      type: 'text',
      renderCell: (row: VacancyData) => (
        <Box sx={{ display: 'flex', gap: '32px' }}>
          <Link
            component="button"
            sx={{
              color: '#00000066',
              fontSize: '14px',
              fontWeight: 500,
              textDecorationColor: '#00000066'
            }}
            onClick={() => navigate(`/rec/vacancy/search/${row.id}/edit`)}
          >
            Edit
          </Link>
          <Link
            component="button"
            sx={{
              color: '#F04438',
              fontSize: '14px',
              fontWeight: 500,
              textDecorationColor: '#F04438'
            }}
          >
            Delete
          </Link>
        </Box>
      ),
    },
  ];

  return (
    <Box className="page-container" sx={{ py: '0 !important' }}>
      <Box className="search-section">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '24px' }}>
          <Typography className="page-title">
            Vacancy Search
          </Typography>
        </Box>
        <Box className="search-bar">
          <div style={{ flex: 1 }}>
            <SelectField
              placeholder="Vacancy Type"
              value={searchParams.vacancyType}
              width="100%"
              onChange={(e) => setSearchParams({ ...searchParams, vacancyType: e.target.value as string })}
              options={[
                { value: 'TAAS', label: 'TAAS' },
                { value: 'EOR', label: 'EOR' },
              ]}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Vacancy Title/Code"
              value={searchParams.vacancyTitle}
              width="100%"
              onChange={(e) => setSearchParams({ ...searchParams, vacancyTitle: e.target.value, code: e.target.value })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Country"
              value={searchParams.country}
              width="100%"
              onChange={(e) => setSearchParams({ ...searchParams, country: e.target.value })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <DatePickerField
              placeholder="Start"
              value={searchParams.startDate ? dayjs(searchParams.startDate) : null}
              width="100%"
              onChange={(date) => setSearchParams({ ...searchParams, startDate: date || '' })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <DatePickerField
              placeholder="End"
              value={searchParams.endDate ? dayjs(searchParams.endDate) : null}
              width="100%"
              onChange={(date) => setSearchParams({ ...searchParams, endDate: date || '' })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectField
              placeholder="Status"
              value={searchParams.status}
              width="100%"
              onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value as string })}
              options={[
                { value: '0', label: 'Active' },
                { value: '1', label: 'Inactive' },
              ]}
            />
          </div>
          <Box className="search-bar-button-group">
            <Button variant="contained" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              Clear
            </Button>
          </Box>
        </Box>
        {isLoading ? (
          <Loading message="Loading vacancies..." />
        ) : (
          <CustomTable
            columns={columns}
            rows={vacancies}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        )}
      </Box>
    </Box>
  );
};

export default VacancySearch; 